import { CalendarDays, ChevronDown } from 'lucide-react';
import { seesAllMobilisations, hasStaffRole } from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  useMobilisationsList,
  useSwitchMobilisation,
} from '../../hooks/useMobilisations';
import { formatDate } from '../../lib/dateFmt';

/**
 * Header switcher for the active mobilisation. Shown to back-office
 * staff only (hidden from inductees). The list is already scoped by the
 * server to the mobilisations the user may access; only admin/super_admin
 * get the cross-mobilisation "All" option. Selecting a value calls
 * /api/mobilisations/_switch and reloads so server queries re-scope.
 */
export function MobilisationSwitcher() {
  const { data: user } = useCurrentUser();
  const list = useMobilisationsList();
  const sw = useSwitchMobilisation();

  if (!user) return null;
  // Self-service users (inductees, learners) never switch — they only see
  // their own surface.
  if (!hasStaffRole(user.roles)) return null;
  // Induction days plus the ongoing onboarding programme: scoping to the
  // programme is how stations issue kit to day-to-day starters and existing
  // staff outside a mobilisation day.
  const days = (list.data ?? []).filter((m) => m.kind !== 'onboarding');
  const programme = (list.data ?? []).find((m) => m.kind === 'onboarding');
  if (days.length === 0 && !programme) return null;

  const canSeeAll = seesAllMobilisations(user.roles);
  const current = user.current_mobilisation_id ?? '';

  // Full label of the current selection, so hover/screen-reader still get it
  // even when the trigger is width-capped and the text is ellipsised.
  const currentLabel =
    current === ''
      ? 'All mobilisations'
      : current === programme?.id
        ? `${programme.name} (day-to-day)`
        : (() => {
            const m = days.find((d) => d.id === current);
            return m ? `${m.name} (${formatDate(m.date)}, ${m.status})` : 'Active mobilisation';
          })();

  return (
    // min-w-0 lets this be the flex item that gives up width first, so a long
    // mobilisation name shrinks/ellipsises rather than pushing Sign out off
    // the edge of a cramped landscape-tablet header.
    <div className="relative min-w-0 shrink">
      <CalendarDays
        aria-hidden
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
      />
      <select
        value={current}
        onChange={async (e) => {
          const v = e.target.value || null;
          await sw.mutateAsync(v);
          // Force a refresh so cached lists scope to the new mobilisation.
          location.reload();
        }}
        aria-label="Active mobilisation"
        title={currentLabel}
        // Fills the (shrinkable) wrapper and caps the resting width per
        // breakpoint; the browser ellipsises the selected value while the open
        // dropdown still shows each option in full. Kept modest through the
        // landscape-tablet range and only roomier on a true desktop (xl).
        className="h-9 w-full min-w-0 max-w-[150px] appearance-none truncate rounded-token-sm border border-line-strong/15 bg-surface pl-8 pr-7 text-xs font-medium text-fg-muted shadow-token-sm transition-colors hover:bg-surface-2 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-1 sm:max-w-[190px] xl:max-w-[320px]"
      >
        {canSeeAll ? <option value="">All mobilisations</option> : null}
        {programme ? (
          <option value={programme.id}>{programme.name} (day-to-day)</option>
        ) : null}
        {days.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name} ({formatDate(m.date)}, {m.status})
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
      />
    </div>
  );
}
